import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useHistory } from 'react-router-dom'
import { useShouldShowIBportal } from "ib/utils/hooks"
import { logout } from "../../../../redux/actions"
import { useAccounts, useAppState, useOrganization, useUserApplications } from '../../../../utils/hooks'
import appRoutes from '../../../../utils/routes'
import { buildSignoutEvent } from '../../../../utils/floodlightEvents'
import ModalConfirm from "../../../Presentational/ModalConfirm"
import SideBar from "../../../Presentational/SideBar"
import WtrSideBar from "../../../Presentational/SideBar/WtrSideBar"
import { useCompleteOnboardingModal } from "../Deposit/components/CompleteOnboardingNotice"
import { useMenuEntries } from "./items"
import styles from './style.module.scss'
import { DashboardMenuItem } from "./utils"



const prefix = 'dashboard.sidebar'

// THPRT-6264: items reachable while demo activation is pending.
const demoActivationWhitelist = ['support', 'supportRequest', 'faq', 'logout']

const DashboardSideBar = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const { language, redirectedFromWTR, portalAccountDomain, demoActivationPending, loggedIn, userProfile, userIP, liveAccounts } = useAppState()
  const shouldShowIBportal = useShouldShowIBportal()
  const { isDemo } = useAccounts()
  const { incompleteApplication, hasOngoingApplication, simplifiedAppNotApprovedYet } = useUserApplications()
  const { isTMCY } = useOrganization()
  const { noticeModal, setNoticeModalOpened } = useCompleteOnboardingModal('sidebar')
  const [showDemoFundModal, setshowDemoFundModal] = useState(false)
  const menuItems = useMenuEntries()

  // THPRT-6264: during demo activation only Support Request, FAQ and Logout are
  // actionable. 'accounts' (Dashboard) stays visually present but disabled.
  // Stripping children demotes a SubMenu item to a plain MenuItem, whose caption
  // key is `dashboard.sidebar.<name>` -- an object for parents like promotions.
  // Carry the `.caption` translation over so the label survives the demotion.
  const effectiveMenuItems = demoActivationPending
    ? menuItems.map(item => demoActivationWhitelist.includes(item.name)
        ? { ...item, children: item.children?.filter(c => demoActivationWhitelist.includes(c.name)) }
        : {
            ...item,
            disabled: true,
            children: undefined,
            customCaption: item.customCaption
              ?? (item.children?.length ? t(`dashboard.sidebar.${item.name}.caption`) : undefined),
        })
    : menuItems

  const handleItemClick = useCallback((item: DashboardMenuItem) => {
    // THPRT-6264: disabled items still receive clicks from the SideBar component;
    // guard here so analytics and routing are not triggered for blocked items.
    if (demoActivationPending && !demoActivationWhitelist.includes(item.name)) return

    window.dataLayer?.push({
      'event': 'custom_event',
      'event_name': 'left_navigation',
      'navigation_link_name': item.name
    })

    switch (item.name) {
      case 'funds':
        if (isDemo && (incompleteApplication || !hasOngoingApplication)) setshowDemoFundModal(true)
        break
      case 'withdraw':
        if (simplifiedAppNotApprovedYet) setNoticeModalOpened(true)
        break
      case 'transfer':
        if (simplifiedAppNotApprovedYet) setNoticeModalOpened(true)
        break
      case 'faq':
        if (language === 'en' && !isTMCY)
          window.open('https://support.thinkmarkets.com/hc/en-gb', '_blank')
        else
          history.push(appRoutes.dashboard.support.faq)
        break
      case 'supportRequest':
        if (portalAccountDomain === 'TMJP')
          window.open('https://asahimarkets.co.jp/support/contact-us/index.html', '_blank')
        else
          history.push(appRoutes.dashboard.support.clientRequest)
        break
      case 'logout':
        window.dataLayer?.push(buildSignoutEvent({
          referrer: document.referrer,
          loggedIn,
          language,
          portalHost: window.location.hostname,
          pathname: window.location.pathname,
          userId: userProfile?.id ?? null,
          accountId: liveAccounts?.[0]?.account?.accountNumber ?? null,
          email: userProfile?.email ?? null,
        }))
        dispatch(logout())
        history.push(appRoutes.account.login)
        break
      case 'ib':
        if (shouldShowIBportal)
          history.push(appRoutes.ib.dashboard)
        break
      case 'partnerships':
        history.push(appRoutes.dashboard.partnerships)
        break
    }
  }, [demoActivationPending, isDemo, incompleteApplication, hasOngoingApplication, simplifiedAppNotApprovedYet, setNoticeModalOpened, language, isTMCY, history, portalAccountDomain, dispatch, shouldShowIBportal, loggedIn, userProfile, userIP, liveAccounts])

  const handleProceedClick = useCallback(() => {
    setshowDemoFundModal(false)
    if (incompleteApplication) {
      // THPRT-6269: resume in the General/Simplified flow, never the legacy step machine
      history.push(appRoutes.onboarding)
    } else {
      history.push(appRoutes.account.createAccount.live)
    }
  }, [history, incompleteApplication])

  // wtrItems derives from original menuItems (WTR mode is not a demo-activation context)
  const wtrItems = menuItems.find(item => item.name === 'funds')

  return (
    <>

      {redirectedFromWTR ? (
        <WtrSideBar
          handleItemClick={handleItemClick}
          languagePrefix={`${prefix}.funds`}
          items={wtrItems?.children || []}
        />
      ) : (
        <SideBar
          handleItemClick={handleItemClick}
          languagePrefix={prefix}
          items={effectiveMenuItems}
        />
      )}
      {isDemo && showDemoFundModal && (
        <ModalConfirm
          showCross
          isOpen
          icon="attentionWarning"
          title=""
          onModalClose={() => setshowDemoFundModal(false)}
        >
          <div>{incompleteApplication ? t(`${prefix}.funds.demoWarning.textIncomplete`) : t(`${prefix}.funds.demoWarning.text`)}</div>
          <div className={styles.warningButtons}>
            <button
              className="link"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setshowDemoFundModal(false)
              }}
            >
              {t(`${prefix}.funds.demoWarning.later`)}
            </button>

            <button
              className="green"
              type="submit"
              onClick={handleProceedClick}
            >
              {incompleteApplication ? t(`dashboard.account.goLive.completeApplication`) : t(`${prefix}.funds.demoWarning.create`)}
            </button>
          </div>
        </ModalConfirm>
      )}
      {noticeModal}
    </>
  )
}

export default DashboardSideBar
